import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/client";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  deactivateStaff, inviteStaff, listStaff, resendStaffAccess,
  RECOVERY_SENT_NOTE, RECOVERY_STALE_NOTE, accessStatusLabel,
  MFA_RESET_NOTE, MFA_SIGNOUT_FAILED_NOTE, SIGNED_OUT_NOTE,
  type RecoveryAction, type RecoveryResult, type StaffUser,
} from "../api/portal";

/**
 * Scadox staff accounts. Deliberately flat -- every internal user can invite
 * or deactivate any other; portal_audit_log is the accountability mechanism
 * rather than a privilege tier. See the Phase 2 spec's "out of scope".
 */
export function StaffPage({ currentUserId }: { currentUserId?: string }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<StaffUser | null>(null);
  const [pendingReset, setPendingReset] = useState<StaffUser | null>(null);
  const [pendingMfaReset, setPendingMfaReset] = useState<StaffUser | null>(null);
  const [pendingSignOut, setPendingSignOut] = useState<StaffUser | null>(null);
  // A Set, not a single id: recovering one row must not make an earlier
  // row's "Sent" note vanish or its button reappear -- every resend
  // invalidates that user's previous temporary password, so the note is
  // load-bearing per-row, not a single most-recent-action flag.
  // A Map rather than a Set: the note depends on which action completed.
  const [sent, setSent] = useState<Map<string, RecoveryResult>>(new Map());

  const { data, isLoading } = useQuery({ queryKey: ["staff"], queryFn: listStaff });

  // Which note to show depends on what was actually done -- a cleared
  // authenticator, a session ended, and a reissued temporary password have
  // nothing in common except that all three land in this cell.
  const noteFor = (r: RecoveryResult) => {
    if (r.action_taken === "reset_mfa") {
      return r.signed_out === false ? MFA_SIGNOUT_FAILED_NOTE : MFA_RESET_NOTE;
    }
    if (r.action_taken === "sign_out") return SIGNED_OUT_NOTE;
    return RECOVERY_SENT_NOTE;
  };


  const invite = useMutation({
    mutationFn: (email: string) => inviteStaff(email),
    onSuccess: () => {
      setEmail("");
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["staff"] });
    },
    onError: (e) => {
      setError(
        e instanceof ApiError && e.status === 409
          ? "That email already has a portal account — as staff or as a customer user. One email, one identity."
          : "Could not invite this user.",
      );
    },
  });

  const deactivate = useMutation({
    mutationFn: (userId: string) => deactivateStaff(userId),
    onSuccess: () => {
      setError(null);
      setPending(null);
      queryClient.invalidateQueries({ queryKey: ["staff"] });
    },
    onError: (e) => {
      setPending(null);
      setError(
        e instanceof ApiError && e.status === 409
          ? "At least one active staff account must remain."
          : "Could not deactivate this account.",
      );
    },
  });

  const recover = useMutation({
    mutationFn: ({ user, action }: { user: StaffUser; action: RecoveryAction }) =>
      resendStaffAccess(user.user_id, action),
    onSuccess: (result, { user }) => {
      setError(null);
      setPendingReset(null);
      setPendingMfaReset(null);
      setPendingSignOut(null);
      // Replace, not mutate -- React only re-renders on a new Map identity.
      setSent((prev) => new Map(prev).set(user.user_id, result));
      queryClient.invalidateQueries({ queryKey: ["staff"] });
    },
    onError: (e) => {
      setPendingReset(null);
      setPendingMfaReset(null);
      setPendingSignOut(null);
      setError(
        e instanceof ApiError && e.status === 409
          ? RECOVERY_STALE_NOTE
          : "Could not send that email.",
      );
    },
  });

  const staff = data?.staff ?? [];

  return (
    <div>
      <h2>Scadox staff</h2>
      <p className="dim">
        Staff accounts can onboard customers, browse any customer's data, and change
        client settings. Every action they take is recorded in the audit log.
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); invite.mutate(email); }}
        style={{ display: "flex", gap: 8, margin: "12px 0" }}
      >
        <input
          type="email"
          placeholder="Invite staff by email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button className="btn-primary" type="submit" disabled={invite.isPending}>Invite</button>
      </form>

      {error && <p style={{ color: "var(--red)" }}>{error}</p>}
      {isLoading && <p>Loading…</p>}

      <table className="data-table">
        <thead><tr><th>Email</th><th>Status</th><th>Added</th><th /></tr></thead>
        <tbody>
          {staff.map((u) => (
            <tr key={u.user_id}>
              <td>{u.email}{u.user_id === currentUserId && <span className="badge you">you</span>}</td>
              <td>
                {u.active ? "Active" : "Deactivated"}
                {u.active && accessStatusLabel(u.access_status) && (
                  <><br /><span className="dim">{accessStatusLabel(u.access_status)}</span></>
                )}
              </td>
              <td>{new Date(u.created_at).toLocaleDateString()}</td>
              <td>
                {/* No control on your own row -- the API rejects it too, and
                    that check is the real one. */}
                {u.active && u.user_id !== currentUserId && (
                  <button onClick={() => setPending(u)}>Deactivate</button>
                )}
                {u.active && !sent.has(u.user_id) && u.access_status === "pending_first_login" && (
                  <button
                    disabled={recover.isPending}
                    onClick={() => recover.mutate({ user: u, action: "resend_invite" })}
                  >
                    Resend invitation
                  </button>
                )}
                {u.active && !sent.has(u.user_id) && u.access_status === "active" && (
                  <button disabled={recover.isPending} onClick={() => setPendingReset(u)}>
                    Send password reset
                  </button>
                )}
                {/* Not gated on whether a factor is registered. Cognito records
                    no per-user MFA preference when the pool enforces MFA
                    globally, so any such flag is permanently false and gating
                    on it hid this button from everyone. The reset is
                    idempotent -- no factor means factor_deleted: false and the
                    user is still signed out -- so there is nothing to guard. */}
                {u.active && u.access_status === "active" && !sent.has(u.user_id) && (
                  <button type="button" disabled={recover.isPending}
                          onClick={() => setPendingMfaReset(u)}>
                    Reset MFA
                  </button>
                )}
                {/* Not suppressed by `sent`: this is the retry path for a reset
                    that deleted the factor and then failed to end the session,
                    so the row is already marked as acted-on exactly when the
                    button is needed. */}
                {u.active && u.access_status === "active" && (
                  <button type="button" disabled={recover.isPending}
                          onClick={() => setPendingSignOut(u)}>
                    Sign out
                  </button>
                )}
                {sent.has(u.user_id) && (
                  <span className="dim">{noteFor(sent.get(u.user_id)!)}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {pending && (
        <ConfirmDialog
          title={`Deactivate ${pending.email}?`}
          tone="danger"
          confirmLabel="Deactivate staff account"
          busy={deactivate.isPending}
          consequences={[
            "They can no longer log in to the portal.",
            "Their existing audit-log entries are kept and still name them.",
            "Re-adding them later means a fresh invite.",
          ]}
          onCancel={() => setPending(null)}
          onConfirm={() => deactivate.mutate(pending.user_id)}
        />
      )}

      {pendingReset && (
        <ConfirmDialog
          title={`Send a password reset to ${pendingReset.email}?`}
          confirmLabel="Send reset email"
          busy={recover.isPending}
          consequences={[
            "They receive a code by email and choose a new password.",
            "Their current password stops working immediately.",
          ]}
          onCancel={() => setPendingReset(null)}
          onConfirm={() => recover.mutate({ user: pendingReset, action: "reset_password" })}
        />
      )}

      {pendingMfaReset && (
        <ConfirmDialog
          title={`Clear ${pendingMfaReset.email}'s authenticator?`}
          tone="danger"
          confirmLabel="Clear authenticator"
          busy={recover.isPending}
          consequences={[
            "Their registered authenticator is deleted and cannot be restored.",
            "They are signed out, and enroll a new device at their next login.",
            "A page they already have open may keep working for up to an hour.",
            "Their password is unchanged.",
          ]}
          onCancel={() => setPendingMfaReset(null)}
          onConfirm={() => recover.mutate({ user: pendingMfaReset, action: "reset_mfa" })}
        />
      )}

      {pendingSignOut && (
        <ConfirmDialog
          title={`Sign ${pendingSignOut.email} out of the portal?`}
          confirmLabel="Sign this user out"
          busy={recover.isPending}
          consequences={[
            "Their open sessions stop working and they return to the login page.",
            "A page they already have open may keep working for up to an hour.",
            "Their password and authenticator are unchanged.",
          ]}
          onCancel={() => setPendingSignOut(null)}
          onConfirm={() => recover.mutate({ user: pendingSignOut, action: "sign_out" })}
        />
      )}
    </div>
  );
}
